import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { BookOpen, Edit, Plus, Save, X, ArrowUp, ArrowDown, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptySection = () => ({ title: '', body: '' });

const RulesPage = () => {
  const { user, token } = useAuth();
  const [rules, setRules] = useState({ intro: '', sections: [], updated_at: null });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({ intro: '', sections: [] });

  const isPresident = user?.role === 'president';

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const res = await axios.get(`${API}/rules`);
      setRules(res.data);
    } catch (err) {
      console.error('Failed to load rules', err);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = () => {
    setDraft({
      intro: rules.intro || '',
      sections: rules.sections && rules.sections.length > 0
        ? rules.sections.map(s => ({ title: s.title || '', body: s.body || '' }))
        : [emptySection()]
    });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft({ intro: '', sections: [] });
  };

  const updateSection = (idx, field, value) => {
    const next = [...draft.sections];
    next[idx] = { ...next[idx], [field]: value };
    setDraft({ ...draft, sections: next });
  };

  const addSection = () => {
    setDraft({ ...draft, sections: [...draft.sections, emptySection()] });
  };

  const removeSection = (idx) => {
    setDraft({ ...draft, sections: draft.sections.filter((_, i) => i !== idx) });
  };

  const moveSection = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= draft.sections.length) return;
    const next = [...draft.sections];
    [next[idx], next[target]] = [next[target], next[idx]];
    setDraft({ ...draft, sections: next });
  };

  const handleSave = async () => {
    // Filter out fully-empty sections
    const cleaned = draft.sections.filter(s => (s.title || '').trim() || (s.body || '').trim());
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/rules`,
        { intro: draft.intro || '', sections: cleaned },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRules(res.data);
      setEditing(false);
      toast.success('Rules updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rules');
    } finally {
      setSaving(false);
    }
  };

  const hasContent = (rules.intro && rules.intro.trim()) || (rules.sections && rules.sections.length > 0);

  return (
    <Layout>
      <SEO
        title="Club Rules"
        description="Official rules and guidelines for the Manitoulin Horse Enthusiasts Club."
        url="/rules"
      />

      {/* Header */}
      <div className="bg-primary text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-display text-4xl font-bold mb-2">Club Rules</h1>
              <p className="text-white/80">Official rules and guidelines for MHEC members</p>
            </div>
            {isPresident && !editing && (
              <Button
                onClick={startEdit}
                className="bg-white text-primary hover:bg-stone-100"
                data-testid="edit-rules-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Rules
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : editing ? (
          <Card className="border-stone-200" data-testid="rules-editor">
            <CardContent className="p-6 space-y-6">
              {/* Intro */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Introduction (optional)</Label>
                <Textarea
                  value={draft.intro}
                  onChange={(e) => setDraft({ ...draft, intro: e.target.value })}
                  placeholder="A short introduction shown above the rule sections..."
                  className="bg-stone-50 min-h-24"
                  data-testid="rules-intro-input"
                />
              </div>

              {/* Sections */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Sections</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addSection}
                    data-testid="add-section-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Section
                  </Button>
                </div>

                {draft.sections.length === 0 && (
                  <p className="text-sm text-stone-500 italic">
                    No sections yet. Click &quot;Add Section&quot; to create one.
                  </p>
                )}

                {draft.sections.map((section, idx) => (
                  <Card key={idx} className="border-stone-200 bg-stone-50/40">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-xs text-stone-600">Section {idx + 1}</Label>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => moveSection(idx, -1)}
                            disabled={idx === 0}
                            className="h-8 w-8 p-0"
                            aria-label="Move up"
                          >
                            <ArrowUp className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => moveSection(idx, 1)}
                            disabled={idx === draft.sections.length - 1}
                            className="h-8 w-8 p-0"
                            aria-label="Move down"
                          >
                            <ArrowDown className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeSection(idx)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            aria-label="Remove section"
                            data-testid={`remove-section-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <Input
                        value={section.title}
                        onChange={(e) => updateSection(idx, 'title', e.target.value)}
                        placeholder="Section title (e.g., General Rules)"
                        className="bg-white font-medium"
                        data-testid={`section-title-${idx}`}
                      />
                      <Textarea
                        value={section.body}
                        onChange={(e) => updateSection(idx, 'body', e.target.value)}
                        placeholder="Body of this section. Line breaks are preserved."
                        className="bg-white min-h-32"
                        data-testid={`section-body-${idx}`}
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2 border-t border-stone-200">
                <Button variant="outline" onClick={cancelEdit} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-primary"
                  data-testid="save-rules-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Rules
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : !hasContent ? (
          <Card className="border-stone-200">
            <CardContent className="py-16 text-center">
              <BookOpen className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-stone-700 mb-2">No Rules Published Yet</h2>
              <p className="text-stone-500">
                {isPresident
                  ? 'Click "Edit Rules" above to add the first rules for your club.'
                  : 'Club rules will be posted here once published by the president.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6" data-testid="rules-content">
            {rules.intro && rules.intro.trim() && (
              <Card className="border-stone-200 bg-primary/5">
                <CardContent className="p-6">
                  <p className="whitespace-pre-wrap text-stone-800 leading-relaxed">{rules.intro}</p>
                </CardContent>
              </Card>
            )}

            {rules.sections && rules.sections.map((section, idx) => (
              <Card key={idx} className="border-stone-200" data-testid={`rules-section-${idx}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 text-sm font-bold">
                      {idx + 1}
                    </div>
                    <h2 className="font-display text-2xl font-semibold text-stone-900">
                      {section.title}
                    </h2>
                  </div>
                  <div className="pl-11 text-stone-700 whitespace-pre-wrap leading-relaxed">
                    {section.body}
                  </div>
                </CardContent>
              </Card>
            ))}

            {rules.updated_at && (
              <p className="text-xs text-stone-500 text-right flex items-center justify-end gap-1">
                <Check className="w-3 h-3" />
                Last updated {new Date(rules.updated_at).toLocaleDateString('en-US', {
                  year: 'numeric', month: 'long', day: 'numeric'
                })}
              </p>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default RulesPage;
